import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../client';
import type {
  EnterpriseLearner,
  EnterpriseLearnerDetail,
  PaginatedEnterpriseLearnerList,
} from '../generated';

export type { EnterpriseLearner, EnterpriseLearnerDetail, PaginatedEnterpriseLearnerList };

export interface EnterpriseLearnerParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: 'invited' | 'active' | 'archived';
  team?: string;
}

export function useEnterpriseLearners(
  enterpriseUuid: string | undefined,
  params?: EnterpriseLearnerParams
) {
  return useQuery<PaginatedEnterpriseLearnerList, ApiError>({
    queryKey: ['enterprise-learners', enterpriseUuid, params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedEnterpriseLearnerList>(
        `/enterprises/${enterpriseUuid}/learners/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export interface InviteLearnerPayload {
  first_name: string;
  last_name: string;
  email: string;
}

export function useInviteLearner(enterpriseUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation<EnterpriseLearner, ApiError, InviteLearnerPayload>({
    mutationFn: async (data) => {
      const response = await apiClient.post<EnterpriseLearner>(
        `/enterprises/${enterpriseUuid}/learners/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enterprise-learners', enterpriseUuid] });
    },
  });
}

export function useRemoveLearner(enterpriseUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (learnerUuid: string) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/learners/${learnerUuid}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enterprise-learners', enterpriseUuid] });
      qc.invalidateQueries({ queryKey: ['misc', 'learners', 'list'] });
    },
  });
}

export function useResendWelcome(enterpriseUuid: string | undefined) {
  return useMutation<void, ApiError, string>({
    mutationFn: async (learnerUuid: string) => {
      await apiClient.post(
        `/enterprises/${enterpriseUuid}/learners/${learnerUuid}/resend-welcome/`,
        {}
      );
    },
  });
}

export interface UpdateLearnerPayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  username?: string;
  contact_number?: string;
  team_uuids?: string[];
  sector_id?: string;
  role_category?: string;
  employment_status?: string;
  verifications?: Record<string, string>;
  job_title?: string;
  department?: string;
  status?: 'invited' | 'active' | 'archived';
}

export function useUpdateLearner(enterpriseUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    EnterpriseLearner,
    ApiError,
    { learnerUuid: string; data: UpdateLearnerPayload; enterpriseUuid?: string }
  >({
    mutationFn: async ({ learnerUuid, data, enterpriseUuid: overrideUuid }) => {
      const uuid = overrideUuid ?? enterpriseUuid;
      const response = await apiClient.patch<EnterpriseLearner>(
        `/enterprises/${uuid}/learners/${learnerUuid}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { learnerUuid, enterpriseUuid: overrideUuid }) => {
      const uuid = overrideUuid ?? enterpriseUuid;
      qc.invalidateQueries({ queryKey: ['enterprise-learners', uuid] });
      qc.invalidateQueries({ queryKey: ['enterprise-learner-detail', uuid, learnerUuid] });
      qc.invalidateQueries({ queryKey: ['misc', 'learners', 'list'] });
    },
  });
}

export function useEnterpriseLearnerDetail(
  enterpriseUuid: string | undefined,
  learnerUuid: string | undefined
) {
  return useQuery<EnterpriseLearnerDetail, ApiError>({
    queryKey: ['enterprise-learner-detail', enterpriseUuid, learnerUuid],
    queryFn: async () => {
      const response = await apiClient.get<EnterpriseLearnerDetail>(
        `/enterprises/${enterpriseUuid}/learners/${learnerUuid}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!learnerUuid,
  });
}

// ---------------------------------------------------------------------------
// Move learners between companies (FE-E5.3 / BE-E5.3)
// ---------------------------------------------------------------------------

export interface MoveLearnersPayload {
  target_enterprise_uuid: string;
  learner_uuids: string[];
}

export interface MoveLearnersError {
  learner_uuid: string;
  reason: string;
}

export interface MoveLearnersResult {
  total: number;
  moved: number;
  skipped: number;
  errors: MoveLearnersError[];
}

/**
 * Moves one or more learners from the source enterprise to a target enterprise
 * (BE-E5.3, `POST /enterprises/{source}/learners/move-company/`). Distinct from
 * the within-company team move. Requires authority in both companies server-side
 * (`learner.delete` on source + `learner.create` on target).
 */
export function useMoveLearnersToCompany(sourceEnterpriseUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation<MoveLearnersResult, ApiError, MoveLearnersPayload>({
    mutationFn: async (data) => {
      const response = await apiClient.post<MoveLearnersResult>(
        `/enterprises/${sourceEnterpriseUuid}/learners/move-company/`,
        data
      );
      return response.data;
    },
    onSuccess: (_result, { target_enterprise_uuid }) => {
      qc.invalidateQueries({ queryKey: ['enterprise-learners', sourceEnterpriseUuid] });
      qc.invalidateQueries({ queryKey: ['enterprise-learners', target_enterprise_uuid] });
      qc.invalidateQueries({ queryKey: ['misc', 'learners', 'list'] });
    },
  });
}

export function useRestoreLearner(enterpriseUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation<EnterpriseLearner, ApiError, string>({
    mutationFn: async (learnerUuid: string) => {
      const response = await apiClient.post<EnterpriseLearner>(
        `/enterprises/${enterpriseUuid}/learners/${learnerUuid}/restore/`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enterprise-learners', enterpriseUuid] });
    },
  });
}
